import {PinnableHeaderTableCell} from '@hconnect/uikit'
import {Table, TableBody, TableHead, TableCell, TableRow, TableContainer} from '@mui/material'
import {createRef, useCallback, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {
  SLA_TABLE_WIDTHS,
  SLA_TABLE_LEFTS,
  SLA_TABLE_COLUMN_KEYS,
  FEATURE_NAMESPACE
} from '../../consts'
import {mapKpiTabValuesToSlaTableDataItems} from '../../mappers'
import {TabTypeDto, KpiTabValuesDto} from '../../types'
import {getMonthsDetails} from '../../utils'

import {SlaKpiTabsValuesTableRow, SlaKpiTabsValuesTableRowProps} from './SlaKpiTabsValuesTableRow'

export type SlaKpiTabsValuesTableProps = {
  data: KpiTabValuesDto
  tabType: TabTypeDto
  isDesktop: boolean
  onOpenCommentsVersions: (kpiId: string) => void
  cceKpiNameMap: Map<number, string>
} & Pick<
  SlaKpiTabsValuesTableRowProps,
  'isCellEditable' | 'isCellCreatable' | 'getIsExpanded' | 'toggleExpandCollapse'
>

export const SlaKpiTabsValuesTable = ({
  data,
  tabType,
  isDesktop,
  isCellEditable,
  isCellCreatable,
  getIsExpanded,
  toggleExpandCollapse,
  onOpenCommentsVersions,
  cceKpiNameMap
}: SlaKpiTabsValuesTableProps) => {
  const {t, i18n} = useTranslation(FEATURE_NAMESPACE)
  const language = i18n.language
  const [isScrolledToRight, setIsScrolledToRight] = useState(false)
  const tableContainerRef = createRef<HTMLDivElement>()

  const handleScroll = useCallback((event) => {
    const target = event.target as HTMLDivElement
    setIsScrolledToRight(target.scrollLeft > 0)
  }, [])

  const monthDetails = getMonthsDetails(language)
  const rows = mapKpiTabValuesToSlaTableDataItems(data)

  return (
    <TableContainer onScroll={handleScroll} ref={tableContainerRef}>
      <Table sx={{width: '100%', tableLayout: 'fixed'}} data-test-id={`tabvalues-table-${tabType}`}>
        <TableHead>
          <TableRow>
            <PinnableHeaderTableCell
              width={SLA_TABLE_WIDTHS.DATA_FIELD}
              left={SLA_TABLE_LEFTS.DATA_FIELD}
              showShadow={!isDesktop && isScrolledToRight}
              isSticky={true}
            >
              {t('label.dataField')}
            </PinnableHeaderTableCell>
            <PinnableHeaderTableCell
              width={SLA_TABLE_WIDTHS.TARGET}
              isSticky={isDesktop}
              left={isDesktop ? SLA_TABLE_LEFTS.TARGET : undefined}
              showShadow={isDesktop && isScrolledToRight}
            >
              {t('label.slaTarget')}
            </PinnableHeaderTableCell>
            {monthDetails.names.map((monthName) => (
              <TableCell
                sx={{width: `${SLA_TABLE_WIDTHS.MONTHS}px`}}
                key={`tabvalues-table-${tabType}-${monthName}`}
              >
                {monthName}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length > 0 ? (
            rows.map((row) => (
              <SlaKpiTabsValuesTableRow
                row={row}
                getIsExpanded={getIsExpanded}
                toggleExpandCollapse={toggleExpandCollapse}
                key={`tabvalues-table-${tabType}-kpidef-${row.id}`}
                isScrolledToRight={isScrolledToRight}
                isDesktop={isDesktop}
                isCellEditable={isCellEditable}
                isCellCreatable={isCellCreatable}
                onOpenCommentsVersions={onOpenCommentsVersions}
                cceKpiNameMap={cceKpiNameMap}
              />
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={SLA_TABLE_COLUMN_KEYS.length} sx={{textAlign: 'center'}}>
                {t('message.noData')}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
